// =============================================================
// TaskDraftModal.jsx — a proposed task, opened for editing BEFORE anything is
// created.
//
// "Add task" used to create the proposal exactly as Harvi drafted it, which put
// the reader in the wrong place: the model gets a title's wording, an assignee
// or a due date wrong often enough that the fix belongs BEFORE the row exists,
// not in the task list afterwards. So Add opens the draft with every field
// editable and the line it came from in view, and SAVE is the first moment a
// task exists.
//
// IT IS STILL AN ORDINARY CREATE. `POST /api/tasks` under the reader's own
// token, so the Admin gate, the validation, the audit, the notifications and
// the near-duplicate gate all apply — the gate's 409 is caught HERE and shown
// inside the dialog, because being bounced back to a list to decide about a
// duplicate you cannot see is not a decision anybody can make.
//
// THE ASSIGNEE IS PRE-FILLED WITH THE READER, and that is honest rather than
// convenient: `createTaskSvc` defaults an unassigned task to its creator ("never
// nobody"), so leaving the field blank does not mean nobody — it means you.
// Showing that up front is the difference between a default and a surprise.
// =============================================================

import { useState, useContext } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { UserContext } from '../../context/UserContext';
import { useApi } from '../../hooks/useApi';
import { Btn, Modal, ModalFooter, Input, Textarea, Select, useToast } from '../ui';
import { AssigneePicker } from './AssigneePicker';
import { InlineSearchPicker, useDealPickerItems, EDITABLE_STATUSES, dealPickerValueFor } from '../tasks/TaskComponents';
import { scopeFromPick } from './DealScopePicker';

const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

// The picker's combo value for a draft's scope — a project wins over its
// detail, a detail over its deal (the most specific row in the one list).
const comboFor = (dealId, dealDetailId, projectId) => dealPickerValueFor({ dealId, dealDetailId, projectId });

export const TaskDraftModal = ({ draft, users, contacts, onCancel, onSaved }) => {
  const { toast } = useToast();
  const me = useContext(UserContext);
  const { dealPickerItems } = useDealPickerItems({ includeProjects: true });

  // The roster is fetched here when the caller has none — the global pop-up is
  // mounted on every page and has no people list of its own, and a surface that
  // cannot show an assignee picker would make the editor useless exactly where
  // it is most needed. A caller that already has them (the notes feed) passes
  // them and these fetches are skipped.
  // An EMPTY list counts as none: a parent still loading its roster passes [],
  // and treating that as "the caller has them" left the picker with nobody to
  // show and the seeded assignee with no name to render.
  const haveUsers    = Array.isArray(users) && users.length > 0;
  const haveContacts = Array.isArray(contacts) && contacts.length > 0;
  const { data: fetchedUsers }    = useApi(() => (haveUsers ? Promise.resolve(null) : api.get('users')), [haveUsers]);
  const { data: fetchedContacts } = useApi(() => (haveContacts ? Promise.resolve(null) : api.get('contacts')), [haveContacts]);
  const people    = haveUsers ? users : (fetchedUsers || []);
  const contactly = haveContacts ? contacts : (fetchedContacts || []);

  const [title, setTitle]             = useState(draft?.title || '');
  const [description, setDescription] = useState(draft?.description || '');
  const [dueDate, setDueDate]         = useState((draft?.dueDate || '').slice(0, 10));
  const [priority, setPriority]       = useState(
    PRIORITIES.includes(draft?.priority) ? draft.priority : 'Medium');
  const [status, setStatus]           = useState('Not Started');
  const [dealId, setDealId]           = useState(draft?.dealId || '');
  const [dealDetailId, setDealDetailId] = useState(draft?.dealDetailId || '');
  const [projectId, setProjectId]     = useState(draft?.projectId || '');
  const [combo, setCombo]             = useState(comboFor(draft?.dealId, draft?.dealDetailId, draft?.projectId));
  const [assignees, setAssignees]     = useState(() => {
    const proposed = (draft?.assigneeIds || []).filter(Boolean).map(id => ({ type: 'user', id }));
    if (proposed.length) return proposed;
    return me?.userID ? [{ type: 'user', id: me.userID }] : [];
  });

  const [saving, setSaving] = useState(false);
  // The near-duplicate gate's answer, shown in place rather than as a bounce.
  const [similar, setSimilar] = useState(null);

  const setScope = (value) => {
    setCombo(value || '');
    const scope = scopeFromPick(value, dealPickerItems);
    setDealId(scope.dealId);
    setDealDetailId(scope.dealDetailId);
    setProjectId(scope.projectId);
  };

  // Editing anything retires a refusal about the last attempt — the same rule
  // the capital-schedule grid follows, because a warning left standing over a
  // field the reader has since changed is about a save that no longer exists.
  const edited = (fn) => (v) => { if (similar) setSimilar(null); fn(v); };

  const save = async (confirmDuplicate = false) => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const created = await api.post('tasks', {
        title: title.trim(),
        description: description.trim() || null,
        priority,
        status,
        dueDate: dueDate || null,
        assigneeIds:        assignees.filter(a => a.type === 'user').map(a => a.id),
        contactAssigneeIds: assignees.filter(a => a.type === 'contact').map(a => a.id),
        dealId: dealId || null,
        dealDetailId: dealDetailId || null,
        projectId: projectId || null,
        sourceNoteId: draft?.sourceNoteId || null,
        // Held by the caller for the life of the proposal, so a retried save
        // (a timeout, a "Save anyway") dedupes instead of creating twice.
        clientRequestId: draft?.clientRequestId,
        ...(confirmDuplicate ? { confirmDuplicate: true } : {}),
      });
      await onSaved?.(created);
    } catch (e) {
      if (e.status === 409 && e.body?.similarTasks?.length) { setSimilar(e.body.similarTasks); }
      else toast.error(e.message || 'Could not create the task.');
    } finally { setSaving(false); }
  };

  return (
    <Modal title="New task from a note" width={560} onClose={onCancel}>
      {draft?.sourceExcerpt && (
        <div style={{ borderLeft: `3px solid ${B.tealDk}`, background: B.surfaceMuted, borderRadius: '0 8px 8px 0', padding: '9px 12px', marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: B.textMd, letterSpacing: 0.4, textTransform: 'uppercase' }}>
            From the note{draft.dealName ? ` · ${draft.dealName}` : ''}
          </div>
          <div style={{ fontSize: 12, color: B.text, fontStyle: 'italic', marginTop: 3, lineHeight: 1.5 }}>
            “{draft.sourceExcerpt}”
          </div>
        </div>
      )}

      <Input autoFocus label="Title" required value={title} onChange={edited(setTitle)}
        placeholder="What needs doing…" />
      <Textarea label="Description" value={description} onChange={edited(setDescription)} rows={3}
        placeholder="Anything the assignee needs to know…" />

      <InlineSearchPicker label="Deal / Deal Detail / Project" value={combo} onChange={edited(setScope)}
        items={dealPickerItems} placeholder="Search deals, details or projects…" clearLabel="— No deal —" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Input label="Due Date" type="date" value={dueDate} onChange={edited(setDueDate)} />
        <Select label="Priority" value={priority} onChange={edited(setPriority)} options={PRIORITIES} />
      </div>

      <AssigneePicker selected={assignees} onChange={edited(setAssignees)}
        users={people} contacts={contactly} />
      <Select label="Status" value={status} onChange={edited(setStatus)}
        options={EDITABLE_STATUSES.map(s => ({ value: s, label: s }))} />

      {similar && (
        <div style={{ fontSize: 12, color: '#8a6100', background: '#fdf6e3', border: '1px solid #e8d9a8', borderRadius: 6, padding: '9px 11px', marginTop: 4 }}>
          <strong>This looks like a task you already have.</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {similar.slice(0, 3).map(t => <li key={t.taskID || t.title}>{t.title}</li>)}
          </ul>
          <div style={{ marginTop: 6 }}>Change the title, or save it anyway.</div>
        </div>
      )}

      <ModalFooter>
        <Btn variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Btn>
        <Btn disabled={!title.trim() || saving} onClick={() => save(!!similar)}>
          {saving ? 'Saving…' : similar ? 'Save anyway' : 'Save task'}
        </Btn>
      </ModalFooter>
    </Modal>
  );
};
